import Box from '@mui/material/Box'
import { type SxProps, TableBody, TableContainer, TableHead } from '@mui/material'
import TableMU from '@mui/material/Table'
import { type ReactNode, useEffect, useState, type UIEvent, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import { type IScrollbarProps, Scrollbar } from 'shared/ui/Scrollbar'
import { SpinnerLoader } from 'shared/ui/Loader'
import { TableRowHead } from 'shared/ui/Table/ui/TableRowHead/TableRowHead'
import { ExpandedTableRow } from 'shared/ui/Table/ui/ExpandedTableRow/ExpandedTableRow'
import { TableContext } from 'shared/ui/Table/context/context'
import { type TableStore } from 'shared/ui/Table/store/tableStore'
import type { IHeaderLeftContentProps } from 'shared/ui/Table/ui/SelectedContent/HeaderLeftContent/HeaderLeftContent'
import { Pagination } from 'shared/ui/Table/ui/Pagination/Pagination'
import { type Base } from 'models'

import styles from './Table.module.scss'
import { type IRow, type IColumn } from './types'

export interface ITableProps<TRow> {
  columns: IColumn<TRow>[]
  hideExpandColumnsIds?: Array<keyof IRow<TRow> | string>
  rows: IRow<TRow>[]
  rowsCount?: number
  hasMore?: boolean
  isLoading?: boolean
  withCheckbox?: boolean
  withSelectAll?: boolean
  withoutSelectedCount?: boolean
  activeRowId?: number
  rightHeaderContent?: ReactNode
  actionsCenter?: ReactNode
  pinnedColumnNames?: Array<keyof IRow<TRow> | string>
  inActionId?: number | null
  initPage?: number
  limit?: number | null
  startPaginationSize?: number | null
  noPagination?: boolean
  isCheckDate?: boolean
  visibleColumnsIds?: string[]
  noMinWidthColumns?: boolean
  height?: string
  defaultWidthColumn?: number | string
  defaultMinWidthColumn?: number | string
  defaultMaxWidthColumn?: number | string
  onChangePagination?: (page: number, pageSize: number) => void
  onRowClick?: (row: IRow<TRow>) => void
  getRowLink?: (row: IRow<TRow>) => string
  setVisibleColumnsIds?: (ids: string[]) => void
  initVisibleColumnsIds?: (ids: string[]) => void
  onSort?: (field: string | number) => void
  sxTable?: SxProps
  sxBox?: SxProps
  sortOrder?: 'asc' | 'desc'
  sortBy?: string
  bottomTableContent?: ReactNode
  scrollToTOpTrigger?: string
  isHoveredRowActions?: boolean
  isCursor?: boolean
  classNameScroll?: string
  customScrollEvent?: (e: UIEvent) => void
  setRows?: TableStore<IRow<TRow>>['setRows']
  setTotal?: TableStore<IRow<TRow>>['setTotal']
  headerLeftContentProps?: IHeaderLeftContentProps
  headerColSpan?: number
  scrollbarProps?: Partial<Omit<IScrollbarProps, 'ref'>>
  ariaLabel: string
  allVisibleRowsSelected?: boolean
}

export type ITablePropsContent<TRow> = ITableProps<TRow> & {
  onRowSelect?: (row: IRow<TRow>, isSelect: boolean) => void
  selectedIds?: Array<string | number>
  bulkAllMode?: boolean
  toggleSelectAll?: (isSelected: boolean) => void
}

const TableContent = <TRow extends Base>({
  columns,
  rows,
  activeRowId,
  rowsCount,
  hasMore,
  isLoading = false,
  rightHeaderContent,
  pinnedColumnNames = [],
  selectedIds = [],
  inActionId,
  initPage,
  withCheckbox,
  withSelectAll,
  withoutSelectedCount,
  hideExpandColumnsIds,
  noPagination,
  isCheckDate,
  defaultWidthColumn,
  defaultMinWidthColumn,
  defaultMaxWidthColumn,
  visibleColumnsIds,
  startPaginationSize,
  limit,
  height = '100%',
  onRowSelect,
  onRowClick,
  onChangePagination = () => {},
  setVisibleColumnsIds,
  initVisibleColumnsIds,
  getRowLink,
  toggleSelectAll,
  onSort,
  sortOrder,
  sxTable,
  sxBox,
  sortBy,
  bottomTableContent,
  scrollToTOpTrigger,
  customScrollEvent,
  isHoveredRowActions,
  isCursor,
  classNameScroll,
  setRows,
  setTotal,
  headerLeftContentProps,
  headerColSpan,
  scrollbarProps,
  ariaLabel,
  bulkAllMode,
  allVisibleRowsSelected,
}: ITablePropsContent<TRow>) => {
  const ref = useRef(null)
  const [isScrollLeft, setIsScrollLeft] = useState(false)
  const visibleColumns = visibleColumnsIds?.length
    ? columns.filter((column) => visibleColumnsIds.includes(column.field as string))
    : columns

  useEffect(() => {
    if (!visibleColumnsIds?.length && initVisibleColumnsIds) {
      initVisibleColumnsIds(
        columns.filter((column) => !column.isHideByDefault).map((column) => column.field as string)
      )
    }
  }, [columns])

  const pinnedColumns = visibleColumns.filter((column) => pinnedColumnNames.includes(column.field))
  const unpinnedColumns = visibleColumns.filter(
    (column) => !pinnedColumnNames.includes(column.field)
  )
  const withActions = rows.some((item) => item.actionsProps || item.actions)

  const onScroll = (e: React.UIEvent) => {
    const target = e.target as HTMLDivElement
    setIsScrollLeft(!!target.scrollLeft)
  }

  const isSelected = (row: IRow<TRow>) => {
    return selectedIds.includes(Number(row.id))
  }

  useEffect(() => {
    setRows && setRows(rows)
  }, [rows])

  useEffect(() => {
    setTotal && setTotal(rowsCount ?? 0)
  }, [rowsCount])

  return (
    <Box
      sx={{
        flex: '1 1 auto',
        ...sxBox,
      }}
      height={height}
      width='100%'
      display='flex'
      flexDirection='column'
      position='relative'
      ref={ref}
    >
      {isLoading ? (
        <SpinnerLoader ariaLabel={`Table_${ariaLabel}`} />
      ) : (
        <>
          <TableContainer style={{ flex: 1 }}>
            <Scrollbar
              onScroll={(event: React.UIEvent) => {
                onScroll(event)
                customScrollEvent && customScrollEvent(event)
              }}
              scrollToTOpTrigger={scrollToTOpTrigger}
              viewClassName={styles.viewScrollClassName}
              className={classNameScroll}
              {...scrollbarProps}
            >
              <TableMU sx={sxTable}>
                <TableHead>
                  <TableRowHead
                    toggleSelectAll={toggleSelectAll}
                    selected={Boolean(selectedIds.length)}
                    unpinnedColumns={unpinnedColumns}
                    pinnedColumns={pinnedColumns}
                    withCheckbox={withCheckbox}
                    withSelectAll={withSelectAll}
                    columns={columns}
                    visibleColumnsIds={visibleColumnsIds}
                    setVisibleColumnsIds={setVisibleColumnsIds}
                    withActions={withActions}
                    rightHeaderContent={rightHeaderContent}
                    isScrollLeft={isScrollLeft}
                    onSort={onSort}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    headerLeftContentProps={headerLeftContentProps}
                    headerColSpan={headerColSpan}
                    withoutSelectedCount={withoutSelectedCount}
                    bulkAllMode={bulkAllMode}
                    allVisibleRowsSelected={allVisibleRowsSelected}
                  />
                </TableHead>

                <TableBody aria-label='TableBody'>
                  {rows.map((row) => (
                    <ExpandedTableRow
                      key={row.id}
                      row={row}
                      selected={isSelected(row)}
                      inAction={inActionId === row.id}
                      withCheckbox={withCheckbox}
                      pinnedColumns={pinnedColumns}
                      unpinnedColumns={unpinnedColumns}
                      onRowSelect={onRowSelect}
                      onRowClick={onRowClick}
                      activeRowId={activeRowId}
                      isCheckDate={isCheckDate}
                      getRowLink={getRowLink}
                      hideExpandColumnsIds={hideExpandColumnsIds}
                      defaultWidthColumn={defaultWidthColumn}
                      defaultMaxWidthColumn={defaultMaxWidthColumn}
                      defaultMinWidthColumn={defaultMinWidthColumn}
                      isScrollLeft={isScrollLeft}
                      withActions={withActions}
                      isHoveredRowActions={isHoveredRowActions}
                      isCursor={isCursor}
                      refTable={ref}
                    />
                  ))}
                  {bottomTableContent}
                </TableBody>
              </TableMU>
            </Scrollbar>
          </TableContainer>
          {!noPagination && (
            <Pagination
              rowsCount={rowsCount}
              hasMore={hasMore}
              itemsCount={rows.length}
              onChange={onChangePagination}
              initPage={initPage}
              limit={limit}
              startSize={startPaginationSize}
            />
          )}
        </>
      )}
    </Box>
  )
}

export const Table = observer(
  <TRow extends Base>({
    store,
    ...props
  }: {
    store?: TableStore<TRow>
  } & ITableProps<TRow>) => {
    return (
      <TableContext.Provider value={(store as unknown as TableStore) || null}>
        <TableContent
          setRows={store?.setRows}
          setTotal={store?.setTotal}
          onSort={store?.onSort}
          sortOrder={store?.sortOrder}
          sortBy={store?.sortBy}
          onRowSelect={store?.toggleSelected}
          selectedIds={store?.selectedIds}
          bulkAllMode={store?.bulkAllMode}
          allVisibleRowsSelected={store?.allVisibleRowsSelected}
          toggleSelectAll={store?.toggleSelectAll}
          visibleColumnsIds={
            store?.withoutDefaultManageColumns ? undefined : store?.visibleColumnsIds
          }
          setVisibleColumnsIds={
            store?.withoutDefaultManageColumns ? undefined : store?.setVisibleColumnsIds
          }
          initVisibleColumnsIds={
            store?.withoutDefaultManageColumns ? undefined : store?.initVisibleColumnsIds
          }
          {...props}
        />
      </TableContext.Provider>
    )
  }
)
